'use client';

import { styled } from 'styled-components';
import Link from 'next/link';
import type { IconNameType } from '@wistia/ui';
import { Icon } from '@wistia/ui';
import cs from 'classnames';
import type { CardColor, CtaProps } from '@types';
import { trackLink } from '@lib/tracking';

type IconCardProps = {
  /**
   * Color scheme of the card
   */
  colorScheme?: Exclude<CardColor, 'lightBlue'>; // Grabbing all card colors except lightBlue, which this component doesn't use!
  /**
   * Title text for the card
   */
  heading: string;
  /**
   * Optional description text for the card
   */
  description?: string;
  /**
   * Icon to use for the card
   */
  icon: IconNameType;
  /**
   * CTA options
   */
  cta?: CtaProps;
};

const IconTextLink = styled.p`
  font-size: ${({ theme }) => theme.typography.fontSize.bodySm};
  font-weight: ${({ theme }) => theme.typography.fontWeight.link};
  margin: 0;

  ${({ theme }) => theme.mq.minWidth.large} {
    font-size: ${({ theme }) => theme.typography.fontSize.desktop.bodySm};
  }
`;

const IconCardContainer = styled.div`
  border-radius: ${({ theme }) => theme.radius.sm};
  display: flex;
  flex-direction: column;
  /* Mobile: tighter icon->heading and description->CTA spacing. */
  gap: ${({ theme }) => theme.spacing['1']};
  padding: ${({ theme }) => theme.spacing['1.5']};

  ${({ theme }) => theme.mq.minWidth.medium} {
    gap: ${({ theme }) => theme.spacing['1.5']};
    padding: ${({ theme }) => theme.spacing['2']};
  }

  &.white {
    background-color: ${({ theme }) => theme.colors.white};
    color: ${({ theme }) => theme.colors.blue800};

    .IconCardContainer--Icon,
    ${IconTextLink} {
      color: ${({ theme }) => theme.colors.blue800};
    }

    &.IconCardContainer--Link {
      &:hover {
        box-shadow: ${({ theme }) => theme.elevation.elevation1()};
      }
    }
  }

  &.darkBlue {
    background-color: ${({ theme }) => theme.colors.blue800};
    color: ${({ theme }) => theme.colors.white};

    .IconCardContainer--Icon {
      color: ${({ theme }) => theme.colors.blue400};
    }

    &.IconCardContainer--Link {
      &:hover {
        box-shadow: ${({ theme }) =>
          theme.elevation.elevation1(theme.colors.blue400)};
      }
    }
  }

  &.blue {
    background-color: ${({ theme }) => theme.colors.blue200};
    color: ${({ theme }) => theme.colors.blue800};

    &.IconCardContainer--Link {
      &:hover {
        box-shadow: ${({ theme }) =>
          theme.elevation.elevation1(theme.colors.blue800)};
      }
    }
  }

  &.purple {
    background-color: ${({ theme }) => theme.colors.purple300};
    color: ${({ theme }) => theme.colors.purple800};

    &.IconCardContainer--Link {
      &:hover {
        box-shadow: ${({ theme }) =>
          theme.elevation.elevation1(theme.colors.purple800)};
      }
    }
  }

  &.pink {
    background-color: ${({ theme }) => theme.colors.pink300};
    color: ${({ theme }) => theme.colors.pink800};

    &.IconCardContainer--Link {
      &:hover {
        box-shadow: ${({ theme }) =>
          theme.elevation.elevation1(theme.colors.pink800)};
      }
    }
  }

  &.orange {
    background-color: ${({ theme }) => theme.colors.orange200};
    color: ${({ theme }) => theme.colors.orange800};

    &.IconCardContainer--Link {
      &:hover {
        box-shadow: ${({ theme }) =>
          theme.elevation.elevation1(theme.colors.orange800)};
      }
    }
  }

  &.yellow {
    background-color: ${({ theme }) => theme.colors.yellow200};
    color: ${({ theme }) => theme.colors.yellow800};

    &.IconCardContainer--Link {
      &:hover {
        box-shadow: ${({ theme }) =>
          theme.elevation.elevation1(theme.colors.yellow800)};
      }
    }
  }

  &.green {
    background-color: ${({ theme }) => theme.colors.green300};
    color: ${({ theme }) => theme.colors.green800};

    &.IconCardContainer--Link {
      &:hover {
        box-shadow: ${({ theme }) =>
          theme.elevation.elevation1(theme.colors.green800)};
      }
    }
  }
`;

const IconCardHeading = styled.h4`
  margin: 0;
`;

const IconCardDescription = styled.p`
  font-size: ${({ theme }) => theme.typography.fontSize.bodySm};
  margin: 0;
  /* Mobile: tighter heading->description spacing. */
  padding-block-start: ${({ theme }) => theme.spacing['0.5']};

  ${({ theme }) => theme.mq.minWidth.medium} {
    padding-block-start: ${({ theme }) => theme.spacing['0.75']};
  }

  ${({ theme }) => theme.mq.minWidth.large} {
    font-size: ${({ theme }) => theme.typography.fontSize.desktop.bodySm};
  }
`;

const IconCard = ({
  colorScheme = 'blue',
  description = null,
  icon,
  cta = null,
  heading,
}: IconCardProps) => {
  const handleClick = () => {
    if (cta.trackingLabel && cta.trackingProperty) {
      trackLink(cta.trackingLabel, cta.trackingProperty);
    }
  };

  return cta ? (
    <IconCardContainer
      as={Link}
      className={cs(colorScheme, 'IconCardContainer--Link')}
      href={cta.href}
      onClick={handleClick}
    >
      <Icon className="IconCardContainer--Icon" size="xl" type={icon} />
      <div>
        <IconCardHeading>{heading}</IconCardHeading>
        {description ? (
          <IconCardDescription>{description}</IconCardDescription>
        ) : null}
      </div>
      <IconTextLink>{cta.text} →</IconTextLink>
    </IconCardContainer>
  ) : (
    <IconCardContainer className={cs(colorScheme)}>
      <Icon className="IconCardContainer--Icon" size="xl" type={icon} />
      <div>
        <IconCardHeading>{heading}</IconCardHeading>
        {description ? (
          <IconCardDescription>{description}</IconCardDescription>
        ) : null}
      </div>
    </IconCardContainer>
  );
};

export default IconCard;
